import { assertNotEmpty } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import { useCallback, useMemo } from 'react'

import { useOperationsContext } from '../../workspace/operationsContext'
import { configurationItemsMapToList } from '../errorsPanel/helpers'
import { groupSearchResults } from './helpers'
import type {
  ConfigurationItemSearchFunctions,
  ConfigurationItemSearchResult,
} from './types'

/**
 * Searches reconciliation specs by their spec ID and by the mapping ID of the GL
 * map they reconcile. Spec sheets are only authored in Excel and are not part of
 * the configuration model, so no class, property or F+ search exists for them.
 */
export const useReconciliationSpecSearch =
  (): ConfigurationItemSearchFunctions => {
    const { reconciliationSpecCreateOperations } = useOperationsContext()
    const translate = useTranslate()

    const allReconciliationSpecs = useMemo(
      () =>
        configurationItemsMapToList(reconciliationSpecCreateOperations)
          .map((indexedReconciliationSpec) => ({
            ...indexedReconciliationSpec,
            operations: indexedReconciliationSpec.operations.filter(
              (operation) => !operation.annulOperations.length,
            ),
          }))
          .filter(({ operations }) => operations.length),
      [reconciliationSpecCreateOperations],
    )

    const searchByName = useCallback(
      (query: string) => {
        const needle = query.toLowerCase().trim()

        return Promise.resolve(
          allReconciliationSpecs
            .filter(
              ({ specId, operations }) =>
                specId.toLowerCase().includes(needle) ||
                operations.some((operation) =>
                  operation.internalOperation.reconciliationSpec?.mappingId
                    ?.toLowerCase()
                    .includes(needle),
                ),
            )
            .map(({ specId, operations, isDraft }) => {
              const latestOperation = operations.at(-1)

              assertNotEmpty(latestOperation)

              return {
                value: {
                  id: specId,
                  type: null,
                  operationType: 'reconciliationSpec',
                  packageName: translate(
                    'reconciliationSpecsTree.fallbackPackage.reconciliationSpecs',
                  ),
                  block: latestOperation.block,
                  layer: latestOperation.layer,
                  isDraft,
                },
              } satisfies ConfigurationItemSearchResult
            }),
        )
      },
      [allReconciliationSpecs, translate],
    )

    const searchByClass = useCallback(() => {
      throw new Error('Searching reconciliation specs by class is unavailable')
    }, [])

    const searchByFPlus = useCallback(() => {
      throw new Error(
        'Searching FPlus within reconciliation specs is unavailable',
      )
    }, [])

    const searchByProperties = useCallback(() => {
      throw new Error(
        'Searching reconciliation specs by properties is unavailable',
      )
    }, [])

    const searchAll = useCallback(
      async (query: string) => {
        const allByName = await searchByName(query)

        return groupSearchResults(...allByName)
      },
      [searchByName],
    )

    return {
      name: searchByName,
      className: searchByClass,
      fplus: searchByFPlus,
      properties: searchByProperties,
      all: searchAll,
    }
  }
